import { type ReactElement, useMemo, useState } from "react";
import { enterDelay } from "../hooks/useListPresence.js";
import { History, LoaderCircle, RefreshCw, Sparkles } from "lucide-react";
import { cn } from "../lib/utils.js";
import { initialsOf } from "../lib/initials.js";
import { Avatar, AvatarFallback } from "../atoms/Avatar/Avatar.js";
import { SegmentedControl } from "../atoms/SegmentedControl/SegmentedControl.js";
import { Skeleton } from "../atoms/Skeleton/Skeleton.js";
import { Spinner } from "../atoms/Spinner/Spinner.js";
import { RailPanelHeader } from "../molecules/RailPanel/RailPanelHeader.js";

/**
 * Whether an audit entry was authored by a person or by AI. Drives the default
 * filter set and the per-row actor visual (person initials vs AI glyph).
 */
/**
 * Who acted. `system` is a service, not a person — canvas projections are
 * written by a system actor, and rendering those with person initials made a
 * background job look like a colleague called "Canvas sync". Purely
 * presentational: it selects a glyph and decides default-filter membership, so
 * this component still learns no module vocabulary (ADR-0009).
 */
export type AuditActorKind = "person" | "ai" | "system";

/**
 * The `audit_class` LIST partition key from ADR-0058: `canvas` for rows that
 * project a Yjs canvas mutation, `application` for everything else. The feed
 * stays data-blind — it never branches on the value, it only lets a caller's
 * filter `match` read it.
 */
export type AuditEntryClass = "application" | "canvas";

/**
 * One row in the {@link AuditFeed}. A pure view-model — the feed is data-blind
 * (ADR-0009 / reuse-first): SOLVE, VSM, Hoshin, and Lama each map their own
 * audit rows onto this shape, so the component carries no module vocabulary.
 */
export interface AuditFeedEntry {
  id: string;
  /** `kind` drives the default People/AI filters and the avatar treatment. */
  actor: { name: string; kind: AuditActorKind };
  /**
   * The unphrased wire action. `action` is human copy ("added a widget"), so a
   * matcher that read the wording would break the moment the wording changed.
   *
   * No filter in this file reads it. The Revert segment was the only one and
   * the ADR-0072 2026-09-10 amendment removed it, so every segment in both
   * `DEFAULT_AUDIT_FILTERS` and `CANVAS_HISTORY_FILTERS` now matches on
   * `actor.kind` or `auditClass`. Optional, and callers may omit it; it is
   * kept so a re-added Revert segment, or any future grouping, has an
   * unphrased action to match without re-deriving one from rendered copy.
   */
  rawAction?: string;
  /** Short action label, e.g. "TARGET UPDATED". */
  action: string;
  /** One-line context, e.g. "Aether Mobility · supplier defects PPM". */
  summary?: string;
  /** Pre-formatted relative/absolute time — the host formats it. */
  timestamp: string;
  /**
   * `audit_class` of the row (ADR-0058), carried so a caller can express a
   * Canvas/Workflow filter without the component learning either word. Optional
   * because the relational modules (Lama, KPI) only ever have one class and
   * their callers map rows that predate this field.
   */
  auditClass?: AuditEntryClass;
}

/**
 * A selectable filter segment. `match` omitted = "show everything" (the All
 * segment); otherwise entries are kept where `match(entry)` is true. The set is
 * a prop so each module supplies its own — LAMA and KPI take the All/People/AI
 * default, the canvas modules take `CANVAS_HISTORY_FILTERS` — without the
 * component hardcoding any module's filters.
 */
export interface AuditFeedFilter {
  id: string;
  label: string;
  match?: (entry: AuditFeedEntry) => boolean;
  /**
   * Replaces the generic "No entries match this filter." when this segment is
   * selected and matches nothing. The copy belongs to the CALLER so the feed
   * stays data-blind: a segment for a capability that does not exist yet has to
   * be able to say so, and the component must not learn which segment that is.
   */
  emptyMessage?: string;
}

/** Default filters when a consumer doesn't pass its own. */
export const DEFAULT_AUDIT_FILTERS: readonly AuditFeedFilter[] = [
  { id: "all", label: "All" },
  { id: "people", label: "People", match: (e) => e.actor.kind === "person" },
  { id: "ai", label: "AI", match: (e) => e.actor.kind === "ai" },
];

/**
 * Opt-in preset for the canvas-backed modules (SOLVE, VSM, Hoshin), which are
 * the only ones whose History mixes both `audit_class` values. Exported rather
 * than defaulted so nothing changes for Lama/KPI: a module with one class would
 * otherwise render a segment that filters nothing.
 *
 * Two axes on one control, by product decision: CLASS (Canvas/Workflow) plus
 * the incumbent PROVENANCE segments (People/AI) these modules already shipped.
 * `SegmentedControl` is `inline-flex flex-wrap`, so the segments WRAP onto a
 * second row at narrow widths rather than clipping — accepted here, and part of
 * why this set is opt-in rather than the default.
 *
 * `Workflow` deliberately matches `application` INCLUDING rows with no class,
 * so pre-cutover canvas rows — written before ADR-0058 classification reached
 * the projection handlers — stay reachable rather than disappearing from both
 * segments.
 *
 * There is deliberately NO `Revert` segment (ADR-0072, 2026-09-10 amendment).
 * One shipped as a placeholder while revert was a separate spike: it matched
 * nothing, it could not match anything, and its only content was "Revert
 * functionality is not available yet." A tab that can never hold a row spends a
 * segment to tell a reader nothing, and it was the sixth — the one pushing the
 * control onto a second line on a rail resizable down to 280px. Removed rather
 * than disabled, the same call the LAMA report row menu made when it dropped
 * Publish instead of showing it greyed out with a reason that would never stop
 * being true.
 *
 * Re-adding it when revert lands is a one-entry change here, and nothing else
 * has to move: `AuditFeedFilter.emptyMessage` stays on the interface, so a
 * caller-supplied set can still carry a segment that explains its own empty
 * result. `AuditFeed.test.tsx` pins BOTH halves of that — that a module can
 * pass its own filters, and that a segment's own `emptyMessage` replaces the
 * generic copy. Until this PR only the generic fallback was asserted, so the
 * field the argument above rests on had a live branch and no test.
 */
export const CANVAS_HISTORY_FILTERS: readonly AuditFeedFilter[] = [
  { id: "all", label: "All" },
  { id: "canvas", label: "Canvas", match: (e) => e.auditClass === "canvas" },
  {
    id: "workflow",
    label: "Workflow",
    match: (e) => e.auditClass !== "canvas",
  },
  { id: "people", label: "People", match: (e) => e.actor.kind === "person" },
  { id: "ai", label: "AI", match: (e) => e.actor.kind === "ai" },
];

/**
 * The two reads a canvas rail merges (ADR-0072), by the names its segments
 * use. The feed itself stays data-blind: the rails pass these to the two
 * helpers below, and the helpers hand `AuditFeed` plain strings.
 */
export type CanvasHistorySource = "workflow" | "canvas";

const CANVAS_HISTORY_SOURCE_NAMES: Record<CanvasHistorySource, string> = {
  workflow: "Workflow",
  canvas: "Canvas",
};

/**
 * The `status` line for a canvas rail showing one read's rows while the other
 * is still in flight (history-rail-latency), or `null` when none is.
 */
export function canvasHistoryLoadingStatus(
  pending: readonly CanvasHistorySource[],
): string | null {
  if (pending.length === 0) return null;
  return pending.length === 1
    ? `${CANVAS_HISTORY_SOURCE_NAMES[pending[0]!]} history is still loading.`
    : "Some history is still loading.";
}

/**
 * The `notice` for a canvas rail whose read failed while rows still show.
 * Names the source when exactly one failed, so the reader knows which half of
 * the history is missing.
 */
export function canvasHistoryFailureNotice(
  failed: readonly CanvasHistorySource[],
): string {
  return failed.length === 1
    ? `${CANVAS_HISTORY_SOURCE_NAMES[failed[0]!]} history couldn’t be loaded, so this list may be incomplete.`
    : "Some history couldn’t be loaded, so this list may be incomplete.";
}

/**
 * The feed's own button treatment, shared by Load more and Retry.
 *
 * One const rather than two copies because the two buttons reporting busy
 * differently is the drift a reader reads as breakage — and an invariant two
 * identical string literals are asked to hold is aspirational, not structural.
 * Hand-rolled rather than the `Button` atom to match the Load-more button that
 * predates this component's Retry; `aria-disabled` (never `disabled`) is what
 * keeps a busy button focusable and announced.
 */
const FEED_BUTTON_CLASS =
  "inline-flex items-center gap-1.5 rounded-md border border-pb-border px-3 py-1.5 text-xs font-medium text-pb-text-secondary hover:pb-overlay-paint focus:outline-none focus-visible:ring-1 focus-visible:ring-pb-teal/50 aria-disabled:cursor-wait aria-disabled:opacity-60";

export interface AuditFeedProps {
  readonly entries: AuditFeedEntry[];
  /** Fixed label beside the clock icon. */
  readonly label?: string;
  /** Inline context on the label line (after "· "), e.g. a business unit or id. */
  readonly subtitle?: string;
  /** Larger heading below the label line, e.g. the entity's full title. */
  readonly heading?: string;
  /** Filter segments; defaults to {@link DEFAULT_AUDIT_FILTERS}. */
  readonly filters?: readonly AuditFeedFilter[];
  readonly isLoading?: boolean;
  /** Cursor page is available; transport remains owned by the host. */
  readonly hasMore?: boolean;
  readonly isLoadingMore?: boolean;
  readonly onLoadMore?: () => void;
  readonly error?: string | null;
  /**
   * Non-blocking "this feed is incomplete" line, shown ABOVE the entries.
   *
   * Distinct from `error`, which REPLACES the list: a feed assembled from
   * several reads can lose one of them and still have rows, and rendering those
   * rows with no warning presents a partial history as a complete one. In a
   * record of what happened that is the more dangerous failure, because nothing
   * on screen looks wrong.
   */
  readonly notice?: string | null;
  /**
   * Non-blocking "more is on its way" line, for a feed assembled from several
   * reads that is showing the rows of those that have answered while another
   * is still in flight (history-rail-latency). Shown ABOVE the rows; with no
   * row to show, it replaces the empty and filter-empty copy, which would
   * otherwise claim there is nothing while a read is still out.
   *
   * It steps aside for `error`, `isLoading` and `notice`: each of those
   * already says something truer about the surface.
   */
  readonly status?: string | null;
  /**
   * Re-run whatever failed. Surfaces a Retry in the blocking `error` box and,
   * inline, in the `notice`.
   *
   * **`notice` does not mean the same thing to every host, and this prop is
   * shared by all of them.** For the canvas rails (SOLVE, VSM, Hoshin) it means
   * a whole stream failed, so rows are MISSING and re-running the feed is
   * exactly the right action. For LAMA and the KPI activity card it means actor
   * NAMES could not be resolved — every row is present, and the names come from
   * a separate roster read that deliberately swallows its own failure. A host in
   * that second group should pass `onRetry` only if its handler actually
   * re-resolves names; binding the feed's refetch there would offer an action
   * that cannot fix what the notice describes.
   *
   * OPTIONAL because that is how a host declines to offer one: "the feed
   * failed" is two cases, and only one of them can be retried. A 5xx settles
   * after a couple of attempts and is worth another; a 403/404 is ADR-0061
   * concealing a deny, settles instantly and never changes, so a button there
   * could only ever repeat the refusal. Hosts pass `undefined` for that case
   * (`isDefinitiveAuditRefusal` in `@playbookai/api-clients` is the one rule),
   * and this component simply renders the failure with no way out — which is
   * honest, because there is none.
   */
  readonly onRetry?: () => void;
  /**
   * A retry is in flight. Disables the button and swaps in a spinner.
   *
   * Needed because TanStack keeps `status: "error"` through a refetch: without
   * this the button sits looking untouched for the whole retry backoff and the
   * reader clicks it again, and again.
   *
   * Flipping this re-announces the surrounding `role="alert"`, which is
   * assertive and implicitly atomic — so a screen-reader user hears the whole
   * failure again on press and again on settle. Accepted rather than
   * overlooked: they just pressed the button, and confirming that something is
   * happening is worth more here than announcement economy.
   */
  readonly isRetrying?: boolean;
  /** Rail close affordance. Omit when the feed is embedded in a page/card. */
  readonly onClose?: () => void;
  /**
   * Focus target after the feed's OWN close button. That button lives INSIDE
   * the rail, so closing from it unmounts the focused element and focus falls
   * to `<body>` — and `WorkspaceRightRail.resolveReturnFocus` cannot help,
   * because it only fires on Escape and the responsive sheet while `children`
   * are rendered untouched. Mirrors `AccessRail`, except optional: a caller
   * that already hands focus back in its own close handler passes nothing.
   */
  readonly resolveReturnFocus?: (() => HTMLElement | null) | undefined;
}

/**
 * Read-only, data-blind activity feed for the workspace right rail. Owns its
 * own header (a fixed label + an inline subtitle + a heading below) and a
 * prop-driven segmented filter; the caller wraps it in a `WorkspaceRightRail`.
 * No revert here (that is a separate spike). PB-243.
 *
 * **Named `AuditFeed`, labelled "History".** PB-4747 renamed the user-facing
 * wording only: people read this as the record of what happened, and "audit"
 * made it sound like a compliance report they had to answer to. The component
 * name, the `audit` right-rail tenant id, the query keys, the API shape and the
 * append-only `audit_log` table are all deliberately unchanged — the storage IS
 * an audit log, and renaming it would be a much larger change for no user gain.
 */
export function AuditFeed({
  entries,
  label = "History",
  subtitle,
  heading,
  filters: filtersProp = DEFAULT_AUDIT_FILTERS,
  isLoading = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  error = null,
  notice = null,
  status = null,
  onRetry,
  isRetrying = false,
  onClose,
  resolveReturnFocus,
}: AuditFeedProps): ReactElement {
  // Fall back to the defaults for an empty set too, so a `filters={[]}` caller
  // never renders an empty control with a stale selection.
  const filters = filtersProp.length ? filtersProp : DEFAULT_AUDIT_FILTERS;

  // Two title slots with a shared fallback: when only one is provided, it shows
  // in both places (inline + heading).
  const inline = subtitle ?? heading;
  const belowHeading = heading ?? subtitle;

  const [activeId, setActiveId] = useState<string>(filters[0]?.id ?? "all");
  const active = filters.find((f) => f.id === activeId) ?? filters[0];
  // Resolved, always-defined value for the controlled SegmentedControl — so a
  // caller swapping the filter set keeps the fallback segment visibly selected.
  const activeValue = active?.id ?? activeId;
  const visible = useMemo(
    () => (active?.match ? entries.filter(active.match) : entries),
    [entries, active],
  );
  const partialStatus = !isLoading && !error && !notice ? status : null;
  const pagination =
    hasMore && onLoadMore ? (
      <div className="mt-4 flex justify-center border-t border-pb-border pt-3">
        <button
          type="button"
          className={FEED_BUTTON_CLASS}
          onClick={() => {
            if (!isLoadingMore) onLoadMore();
          }}
          aria-disabled={isLoadingMore}
        >
          {isLoadingMore ? (
            <LoaderCircle
              className="h-3.5 w-3.5 animate-spin"
              aria-hidden="true"
            />
          ) : null}
          {isLoadingMore ? "Loading…" : "Load more"}
        </button>
      </div>
    ) : null;

  /* The Load-more button's busy mechanics, deliberately identical: the same
     `aria-disabled`, the same click guard that DROPS rather than queues, the
     same spinner. Two buttons in one rail reporting busy differently is the
     kind of drift a reader reads as breakage. */
  const retryButton = onRetry ? (
    <button
      type="button"
      className={FEED_BUTTON_CLASS}
      onClick={() => {
        if (!isRetrying) onRetry();
      }}
      aria-disabled={isRetrying}
    >
      {isRetrying ? (
        <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      ) : null}
      {isRetrying ? "Retrying…" : "Retry"}
    </button>
  ) : null;

  return (
    <div className="flex h-full flex-col">
      {/* The shared rail header (OK-29): the clock glyph leads the title,
          the heading sits under it, and the close is its ghost Button, which
          hands focus back through resolveReturnFocus. */}
      <RailPanelHeader
        leading={
          <History
            className="h-4 w-4 shrink-0 text-pb-text-muted"
            aria-hidden="true"
          />
        }
        title={
          <>
            {label}
            {inline && (
              <>
                {" · "}
                <span className="font-normal text-pb-teal-text">{inline}</span>
              </>
            )}
          </>
        }
        description={belowHeading}
        {...(onClose
          ? {
              onClose,
              closeLabel: `Close ${label}`,
              ...(resolveReturnFocus ? { resolveReturnFocus } : {}),
            }
          : {})}
      />

      {/* One filter is not a choice. A lone segment reads as a control and
          changes nothing when pressed, so a consumer that wants an unfiltered
          feed (LAMA campaigns) gets no bar rather than a decorative one. */}
      {filters.length > 1 ? (
        <div
          role="group"
          aria-label={`Filter ${label.toLowerCase()} entries`}
          className="border-b border-pb-border px-4 py-2"
        >
          <SegmentedControl
            options={filters.map((f) => ({ value: f.id, label: f.label }))}
            value={activeValue}
            onChange={setActiveId}
            size="sm"
            aria-label={`Filter ${label.toLowerCase()} entries`}
          />
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto px-4 py-3">
        {/* Above the content, not instead of it: the rows below are real, there
            are just fewer of them than there should be. Suppressed while loading
            or when `error` already owns the surface. */}
        {notice && !isLoading && !error ? (
          <p
            role="status"
            className="mb-3 rounded-md border border-pb-gold/40 bg-pb-gold/10 p-2 text-xs text-pb-text"
          >
            {notice}
            {/* Inline and underlined rather than the bordered button the error
                box uses — `shell/ModuleHomeStaleNotice` is the shipped
                precedent for a polite notice that still offers a way back.
                Without it "this list may be incomplete" is a dead end: the
                merge counts a failed stream as exhausted, so `hasMore` is false
                and Load more never appears to reach it. */}
            {onRetry ? (
              <>
                {" "}
                <button
                  type="button"
                  className="underline aria-disabled:cursor-wait aria-disabled:opacity-60"
                  onClick={() => {
                    if (!isRetrying) onRetry();
                  }}
                  aria-disabled={isRetrying}
                >
                  {isRetrying ? "Retrying…" : "Retry"}
                </button>
              </>
            ) : null}
          </p>
        ) : null}
        {/* Same slot as the notice, quieter: nothing failed, more is coming. */}
        {partialStatus && visible.length > 0 ? (
          <p
            role="status"
            className="mb-3 flex items-center gap-1.5 text-xs text-pb-text-muted"
          >
            <Spinner size="sm" aria-hidden className="shrink-0" />
            {partialStatus}
          </p>
        ) : null}
        {/* `error` BEFORE `isLoading`, which is `asyncStatus`'s documented order
            (`molecules/AsyncSection`): a failure with nothing on screen wins,
            then loading, then empty. The old order hid a failure behind
            skeletons for as long as the feed stayed pending — and on a
            two-stream rail `isPending` is true while EITHER stream is in
            flight, so an instant 403/404 on one could sit behind a spinner
            until the other answered. No `hasData` guard is needed here: every
            caller already gates `error` on having no rows to lose. */}
        {error ? (
          <div
            role="alert"
            className="rounded-md border border-pb-red/40 bg-pb-red/10 p-3 text-sm text-pb-red-text"
          >
            {error}
            {onRetry ? <div className="mt-2">{retryButton}</div> : null}
          </div>
        ) : isLoading ? (
          <div
            className="space-y-3"
            role="status"
            aria-busy="true"
            aria-label={`Loading ${label.toLowerCase()} entries`}
          >
            {/* Skeleton shimmer, not a blinking block (AP-08). */}
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton shape="circle" className="w-8 shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-2/5" />
                  <Skeleton className="h-3 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <>
            {/* While a read is still out, "nothing here" is not yet known. */}
            <p
              className="px-1 py-6 text-center text-sm text-pb-text-muted"
              {...(partialStatus ? { role: "status" } : {})}
            >
              {partialStatus ??
                active?.emptyMessage ??
                (entries.length === 0
                  ? "No activity yet."
                  : "No entries match this filter.")}
            </p>
            {pagination}
          </>
        ) : (
          <>
            <ol className="m-0 list-none space-y-4 p-0">
              {visible.map((entry, index) => (
                <li
                  key={entry.id}
                  className="pb-enter-row flex gap-3"
                  style={enterDelay(index)}
                >
                  <Avatar
                    size="sm"
                    className={cn(
                      "h-8 w-8 text-xs",
                      entry.actor.kind === "ai" &&
                        "bg-pb-iris-soft text-pb-iris-text ring-1 ring-pb-iris/40",
                      entry.actor.kind === "system" &&
                        "bg-pb-card-elevated text-pb-text-muted ring-1 ring-pb-border",
                    )}
                  >
                    <AvatarFallback
                      className={cn(
                        entry.actor.kind === "ai" && "text-pb-iris-text",
                      )}
                    >
                      {entry.actor.kind === "ai" ? (
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : entry.actor.kind === "system" ? (
                        /* Never initials for a service: initialsOf("Canvas
                           sync") renders "CS", which reads as a person. */
                        <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        initialsOf(entry.actor.name)
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    {/* Actor and action on ONE line: the copy is written to read as
                      a sentence ("Tenant Admin edited the canvas"), and splitting
                      it across two lines made the action look like a separate
                      field rather than the end of that sentence. Not uppercased
                      for the same reason — "ADDED A WIDGET" reads as a machine
                      event rather than something a person did.

                      The whole line truncates as one unit. Truncating only the
                      name would let a long action push the timestamp out of the
                      rail, which is narrow and resizable. */}
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="min-w-0 truncate text-sm">
                        <span className="font-medium text-pb-text">
                          {entry.actor.name}
                        </span>{" "}
                        <span className="text-pb-teal-text">
                          {entry.action}
                        </span>
                      </p>
                      <span className="shrink-0 text-xs text-pb-text-muted pb-tabular">
                        {entry.timestamp}
                      </span>
                    </div>
                    {entry.summary && (
                      <div className="mt-0.5 text-xs leading-relaxed text-pb-text-secondary">
                        {entry.summary}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            {pagination}
          </>
        )}
      </div>
    </div>
  );
}
